/**
 * This file contains the functions to convert between the backend and frontend notification settings data models.
 */

import { BackendPreferences, EmailPreferences, MicrosoftTeamsPreferences, NotificationConfig, NotificationSettings, SlackPreferences } from '../types';

/**
 * Convert backend format to frontend format
 *
 * FRONTEND_FORMAT (flat array):
 * [
 *   { topicId: 'posts', channel: 'email', enabled: true },
 *   { topicId: 'posts', channel: 'slack', enabled: false },
 *   { topicId: 'comments', channel: 'email', enabled: true },
 *   ...
 * ]
 *
 * BACKEND_FORMAT (nested preferences):
 * {
 *   email_preferences: {
 *     send_bonus_emails: true,
 *     send_comment_notifications: true,
 *     ...
 *   },
 *   slack_preferences: {
 *     send_bonus_notifications: false,
 *     ...
 *   },
 * }
 */
export function backendToFrontend(backend: BackendPreferences): NotificationSettings {
	// Start with all settings initialized to false
	const settings = initializeSettings();

	// Update settings based on backend values
	return settings.map((setting) => {
		const mapping = TOPIC_FIELD_MAPPINGS.find((m) => m.topicId === setting.topicId);
		if (!mapping) return setting;

		let enabled = false;

		// Check the appropriate backend field based on channel
		if (setting.channel === 'email' && mapping.emailField) {
			const value = backend.email_preferences[mapping.emailField as keyof EmailPreferences];
			enabled = typeof value === 'boolean' ? value : false;
		} else if (setting.channel === 'slack' && mapping.slackField) {
			enabled = backend.slack_preferences[mapping.slackField as keyof SlackPreferences] ?? false;
		} else if (setting.channel === 'microsoftTeams' && mapping.microsoftTeamsField && backend.microsoft_teams_preferences) {
			enabled = backend.microsoft_teams_preferences[mapping.microsoftTeamsField as keyof MicrosoftTeamsPreferences] ?? false;
		}

		return { ...setting, enabled };
	});
}

/**
 * Convert frontend format to backend format
 */
export function frontendToBackend(frontend: NotificationSettings): BackendPreferences {
	const email_preferences: Partial<EmailPreferences> = {};
	const slack_preferences: Partial<SlackPreferences> = {};
	const microsoft_teams_preferences: Partial<MicrosoftTeamsPreferences> = {};

	// Map each setting back to its backend field
	frontend.forEach((setting) => {
		const mapping = TOPIC_FIELD_MAPPINGS.find((m) => m.topicId === setting.topicId);
		if (!mapping) return;

		if (setting.channel === 'email' && mapping.emailField) {
			(email_preferences as any)[mapping.emailField] = setting.enabled;
		} else if (setting.channel === 'slack' && mapping.slackField) {
			(slack_preferences as any)[mapping.slackField] = setting.enabled;
		} else if (setting.channel === 'microsoftTeams' && mapping.microsoftTeamsField) {
			(microsoft_teams_preferences as any)[mapping.microsoftTeamsField] = setting.enabled;
		}
	});

	return {
		email_preferences: email_preferences as EmailPreferences,
		slack_preferences: slack_preferences as SlackPreferences,
		microsoft_teams_preferences: microsoft_teams_preferences as MicrosoftTeamsPreferences,
	};
}

/**
 * Static configuration of all available notification categories, topics and channels.
 * This should reflect all possible notification settings that can be configured.
 *
 * The form uses this configuration to organize where each notification setting is displayed.
 */
export const NOTIFICATION_CONFIG: NotificationConfig = {
	categories: [
		{
			id: 'your_activity',
			name: 'Your activity',
			topics: [
				{
					id: 'posts',
					name: 'Posts',
					description: 'Recognition that you receive',
					availableChannels: ['email', 'slack', 'microsoftTeams'],
				},
				{
					id: 'comments',
					name: 'Comments',
					description: 'Interaction with posts you commented on or reacted to',
					availableChannels: ['email', 'slack', 'microsoftTeams'],
				},
				{
					id: 'allowance',
					name: 'Allowance',
					description: 'Reminders about your peer-to-peer monthly points',
					availableChannels: ['email', 'slack', 'microsoftTeams'],
				},
				{
					id: 'one_on_ones',
					name: '1:1s',
					description: 'Reminders about upcoming 1:1 meetings with your manager',
					availableChannels: ['email'],
				},
				{
					id: 'feedback',
					name: 'Feedback',
					description: 'Guidance you receive or requests for feedback',
					availableChannels: ['email'],
				},
				{
					id: 'recaps',
					name: 'Recaps',
					description: 'Learn when your recaps are generated or updated',
					availableChannels: ['email'],
				},
				{
					id: 'achievements',
					name: 'Achievements',
					description: 'Personal achievements you earn based on activity',
					availableChannels: ['email'],
				},
			],
		},
		{
			id: 'others_activity',
			name: "Others' activity",
			topics: [
				{
					id: 'trending_posts',
					name: 'Trending posts',
					description: 'Interesting recognition with 3 or more interactions',
					availableChannels: ['email'],
				},
				{
					id: 'work_anniversaries',
					name: 'Work anniversaries',
					description: "Reminders about others' work anniversaries",
					availableChannels: ['slack', 'microsoftTeams'],
				},
				{
					id: 'birthdays',
					name: 'Birthdays',
					description: "Reminders about others' birthdays",
					availableChannels: ['slack', 'microsoftTeams'],
				},
			],
		},
		{
			id: 'team_activity',
			name: "Your team's activity",
			topics: [
				{
					id: 'manager_digest',
					name: 'Manager digest',
					description: "Summary of your direct team's activity last month",
					availableChannels: ['email'],
				},
				{
					id: 'team_recognition_reminders',
					name: 'Recognition reminders',
					description: 'Prompts to recognize each direct report every 30 days',
					availableChannels: ['email', 'slack', 'microsoftTeams'],
				},
				{
					id: 'team_celebrations',
					name: 'Team celebrations',
					description: "Reminders about your team's birthdays, work anniversaries, and new hire celebrations",
					availableChannels: ['slack', 'microsoftTeams'],
				},

				// TODO: these are not implemented yet
				// {
				// 	id: 'leadership_posts',
				// 	name: 'Leadership posts',
				// 	description: "Alert when your manager or other leaders recognize direct reports",
				// 	availableChannels: ['slack', 'microsoftTeams'],
				// },
				// {
				// 	id: 'team_significant_posts',
				// 	name: 'Significant posts',
				// 	description: 'Notified when post a direct report received gets 3 or more interactions',
				// 	availableChannels: ['slack', 'microsoftTeams'],
				// },
				// {
				// 	id: 'team_one_on_ones',
				// 	name: '1:1s',
				// 	description: 'Reminders about upcoming 1:1 meetings with direct reports',
				// 	availableChannels: ['email'],
				// },
				// {
				// 	id: 'team_recaps',
				// 	name: 'Recaps',
				// 	description: "Learn when direct reports' recaps are generated",
				// 	availableChannels: ['email'],
				// }
			],
		},
		{
			id: 'company_activity',
			name: "Company activity",
			topics: [
				{
					id: 'announcements',
					name: 'Announcements',
					description: 'Company news and announcements',
					availableChannels: ['email', 'slack', 'microsoftTeams'],
				},
				{
					id: 'flash_points_events',
					name: 'Flash points events',
					description: 'Limited points allowance to encourage you to give recognition for a special occasion',
					availableChannels: ['email'],
				},
				// TODO: unclear what this setting is
				// {
				// 	id: 'newsletters',
				// 	name: 'Newsletters',
				// 	description: 'Learn about new features, upcoming events and get useful resources',
				// 	availableChannels: ['email'],
				// },
				{
					id: 'pending_rewards',
					name: 'Pending rewards',
					description: 'Alert then someone has claimed a reward that requires approval',
					availableChannels: ['email'],
				},
				{
					id: 'catalog_updates',
					name: 'Rewards catalog updates',
					description: 'Update when new items are added to the catalog',
					availableChannels: ['email'],
				},
				{
					id: 'trial_resources',
					name: 'Trial resources',
					description: 'Get helpful tips throughout duration of your trial',
					availableChannels: ['email'],
				},
			],
		},
	],
};

// Mapping between topic IDs and backend field names
interface TopicFieldMapping {
	topicId: string;
	emailField?: string;
	slackField?: string;
	microsoftTeamsField?: string;
}

/**
 * Flat array mapping between topic IDs and backend field names.
 * This should reflect all possible notification settings that can be configured.
 */
export const TOPIC_FIELD_MAPPINGS: TopicFieldMapping[] = [
	// Your activity
	{
		topicId: 'posts',
		emailField: 'send_bonus_emails',
		slackField: 'send_bonus_notifications',
		microsoftTeamsField: 'send_bonus_notifications',
	},
	{
		topicId: 'comments',
		emailField: 'send_comment_notifications',
		slackField: 'send_bonus_addon_notifications',
		microsoftTeamsField: 'send_bonus_addon_notifications',
	},
	{
		topicId: 'allowance',
		emailField: 'send_reminder_emails',
		slackField: 'send_reminder_notifications',
		microsoftTeamsField: 'send_reminder_notifications',
	},
	{
		topicId: 'one_on_ones',
		emailField: 'send_one_on_one_reminders_and_updates',
	},
	{
		topicId: 'feedback',
		emailField: 'send_constructive_feedback_notifications',
	},
	{
		topicId: 'recaps',
		emailField: 'send_review_recap_reminders_and_updates',
	},
	{
		topicId: 'achievements',
		emailField: 'send_achievement_emails',
	},
	// Others' activity
	{
		topicId: 'trending_posts',
		emailField: 'send_activity_digest',
	},
	{
		topicId: 'work_anniversaries',
		slackField: 'receive_anniversary_notifications',
		microsoftTeamsField: 'receive_anniversary_notifications',
	},
	{
		topicId: 'birthdays',
		slackField: 'receive_birthday_notifications',
		microsoftTeamsField: 'receive_birthday_notifications',
	},
	// Your team's activity
	{
		topicId: 'manager_digest',
		emailField: 'send_manager_digest',
	},
	{
		topicId: 'team_recognition_reminders',
		emailField: 'receive_direct_report_recognition_reminder_notifications',
		slackField: 'receive_direct_report_recognition_reminder_notifications',
		microsoftTeamsField: 'receive_direct_report_recognition_reminder_notifications',
	},
	{
		topicId: 'team_celebrations',
		slackField: 'receive_direct_report_important_celebration_notifications',
		microsoftTeamsField: 'receive_direct_report_important_celebration_notifications',
	},
	// Note: leadership_posts and team_significant_posts may need backend fields added
	// {
	// 	topicId: 'leadership_posts',
	// 	slackField: 'TBD',
	// 	microsoftTeamsField: 'TBD',
	// },
	// {
	// 	topicId: 'team_significant_posts',
	// 	slackField: 'TBD',
	// 	microsoftTeamsField: 'TBD',
	// },
	// {
	// 	topicId: 'team_one_on_ones',
	// 	emailField: 'TBD', // Different from user's own 1:1s
	// },
	// {
	// 	topicId: 'team_recaps',
	// 	emailField: 'TBD', // Different from user's own recaps
	// },

	// Company activity
	{
		topicId: 'announcements',
		emailField: 'send_announcement_emails',
		slackField: 'send_announcement_notifications',
		microsoftTeamsField: 'send_announcement_notifications',
	},
	{
		topicId: 'flash_points_events',
		emailField: 'send_special_program_emails',
	},
	// {
	// 	topicId: 'newsletters',
	// 	emailField: 'TBD', // May need backend field
	// },
	{
		topicId: 'pending_rewards',
		emailField: 'send_claim_notifications',
	},
	{
		topicId: 'catalog_updates',
		emailField: 'send_catalog_updates',
	},
	{
		topicId: 'trial_resources',
		emailField: 'send_trial_reminders',
	},
];

/**
 * Initialize settings from config with all values set to false
 */
export function initializeSettings(): NotificationSettings {
	const settings: NotificationSettings = [];

	NOTIFICATION_CONFIG.categories.forEach((category) => {
		category.topics.forEach((topic) => {
			topic.availableChannels.forEach((channel) => {
				settings.push({
					topicId: topic.id,
					channel,
					enabled: false,
				});
			});
		});
	});

	return settings;
}
